import { useState } from 'react';
import { MessageSquarePlus, X, Send, CheckCircle, ChevronDown } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useLocation } from 'react-router-dom';

const CATEGORIES = [
  { value: 'bug',        label: '🐛 Bug / Something broken' },
  { value: 'recipe',     label: '🍽️ Recipe quality' },
  { value: 'pricing',    label: '💰 Wrong price / cost' },
  { value: 'easy_night', label: '⚡ Easy Night meals' },
  { value: 'feature',    label: '💡 Feature request' },
  { value: 'general',    label: '💬 General feedback' },
];

export default function FeedbackButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open,     setOpen]     = useState(false);
  const [category, setCategory] = useState('general');
  const [message,  setMessage]  = useState('');
  const [email,    setEmail]    = useState('');
  const [rating,   setRating]   = useState<number | null>(null);
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const [catOpen,  setCatOpen]  = useState(false);

  const reset = () => {
    setCategory('general'); setMessage(''); setEmail('');
    setRating(null); setSent(false); setCatOpen(false);
  };

  const handleClose = () => { setOpen(false); setTimeout(reset, 300); };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await supabase.from('feedback').insert({
        user_id:    user?.id ?? null,
        user_email: email || user?.email || null,
        category,
        message:    message.trim(),
        page:       location.pathname,
        rating,
        status:     'new',
      });
      setSent(true);
    } catch { /* silently fail */ }
    setSending(false);
  };

  const selectedCat = CATEGORIES.find(c => c.value === category)!;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => { setOpen(true); setSent(false); }}
        className="fixed bottom-20 right-4 z-40 flex items-center gap-2 bg-primary text-primary-foreground rounded-full shadow-lg px-3.5 py-2.5 text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all"
        aria-label="Send feedback"
      >
        <MessageSquarePlus className="w-4 h-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      {/* Overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={handleClose}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Modal */}
          <div
            className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div>
                <h2 className="font-bold text-foreground text-lg leading-tight">Share Feedback</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Help us make FlyersFeast better</p>
              </div>
              <button onClick={handleClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {sent ? (
              /* Success state */
              <div className="flex flex-col items-center justify-center gap-3 px-5 py-10">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                <p className="font-semibold text-foreground text-lg">Thanks for the feedback!</p>
                <p className="text-sm text-muted-foreground text-center">We read every single one and use it to improve.</p>
                <button onClick={handleClose} className="mt-2 px-5 py-2 bg-primary text-primary-foreground rounded-full text-sm font-medium">
                  Done
                </button>
              </div>
            ) : (
              <div className="px-5 pb-5 flex flex-col gap-4">
                {/* Star rating */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">Overall experience</p>
                  <div className="flex gap-1.5">
                    {[1,2,3,4,5].map(s => (
                      <button key={s} onClick={() => setRating(s === rating ? null : s)}
                        className={`text-2xl transition-transform active:scale-110 ${rating && s <= rating ? 'opacity-100' : 'opacity-30'}`}>
                        ⭐
                      </button>
                    ))}
                  </div>
                </div>

                {/* Category picker */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">Category</p>
                  <div className="relative">
                    <button
                      onClick={() => setCatOpen(!catOpen)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-muted rounded-xl text-sm text-foreground border border-border"
                    >
                      <span>{selectedCat.label}</span>
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${catOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {catOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl z-10 overflow-hidden">
                        {CATEGORIES.map(c => (
                          <button key={c.value} onClick={() => { setCategory(c.value); setCatOpen(false); }}
                            className={`w-full text-left px-3 py-2.5 text-sm hover:bg-muted transition-colors ${c.value === category ? 'text-primary font-medium' : 'text-foreground'}`}>
                            {c.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">Your message <span className="text-red-400">*</span></p>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Tell us what's working, what's broken, or what you'd love to see..."
                    rows={4}
                    className="w-full px-3 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                {/* Email (if not logged in) */}
                {!user && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Email <span className="text-muted-foreground/60">(optional — if you'd like a reply)</span></p>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full px-3 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                )}

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={!message.trim() || sending}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl py-3 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 active:scale-98 transition-all"
                >
                  {sending ? (
                    <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {sending ? 'Sending...' : 'Send Feedback'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
